"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Send, UtensilsCrossed, Clock } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select"
import { useCreateCateringRequestMutation } from "../store"
import { toast } from "sonner"

interface CateringRequestFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CateringRequestForm({ open, onOpenChange }: CateringRequestFormProps) {
  const [createCateringRequest, { isLoading }] = useCreateCateringRequestMutation()
  
  const [formData, setFormData] = useState({
    // Contact Information
    fullName: "",
    phoneNumber: "",
    email: "",
    preferredContactMethod: "",
    
    // Event Details
    eventDate: "",
    eventTime: "",
    eventLocation: "",
    numberOfGuests: "",
    
    // Catering Details
    serviceType: "",
    menuPreference: "",
    specificDishes: "",
    servingStyle: "",
    budgetRange: "",
    specialNotes: "",
    referralSource: "",
  })

  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!formData.referralSource) {
      toast.error("Please select how you heard about us")
      return
    }

    try {
      await createCateringRequest({
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        preferredContactMethod: formData.preferredContactMethod,
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        eventLocation: formData.eventLocation,
        numberOfGuests: parseInt(formData.numberOfGuests, 10),
        serviceType: formData.serviceType,
        menuPreference: formData.menuPreference,
        specificDishes: formData.specificDishes || undefined,
        servingStyle: formData.servingStyle,
        budgetRange: formData.budgetRange,
        specialNotes: formData.specialNotes || undefined,
        referralSource: formData.referralSource,
      }).unwrap()

      setIsSubmitted(true)
      
      // Reset form after showing success
      setTimeout(() => {
        setIsSubmitted(false)
        setFormData({
          fullName: "",
          phoneNumber: "",
          email: "",
          preferredContactMethod: "",
          eventDate: "",
          eventTime: "",
          eventLocation: "",
          numberOfGuests: "",
          serviceType: "",
          menuPreference: "",
          specificDishes: "",
          servingStyle: "",
          budgetRange: "",
          specialNotes: "",
          referralSource: "",
        })
        onOpenChange(false)
      }, 3000)
    } catch (error: any) {
      toast.error(error?.data?.error || "Failed to submit request. Please try again.")
    }
  }

  if (isSubmitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="rounded-full bg-green-100 dark:bg-green-900/20 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Send className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Request Submitted!</h3>
            <p className="text-muted-foreground mb-2">
              Thank you for your catering request. We've received your information and will confirm availability and pricing.
            </p>
            <p className="text-sm text-muted-foreground font-medium">
              We'll contact you via your preferred method: {formData.preferredContactMethod || "Email"}
            </p>
          </motion.div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-amber-500/10 p-2">
              <UtensilsCrossed className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <DialogTitle className="text-2xl">Catering Service Request</DialogTitle>
              <DialogDescription>
                Fill out the form below and we'll get back to you with availability and pricing.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  placeholder="0901 710 4173"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preferredContactMethod">Preferred Method of Contact *</Label>
                <Select
                  value={formData.preferredContactMethod}
                  onValueChange={(value) => handleSelectChange("preferredContactMethod", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Event Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Event Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eventDate">Event Date *</Label>
                <Input
                  id="eventDate"
                  name="eventDate"
                  type="date"
                  value={formData.eventDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eventTime">Event Time *</Label>
                <Input
                  id="eventTime"
                  name="eventTime"
                  type="time"
                  value={formData.eventTime}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eventLocation">Event Location *</Label>
                <Input
                  id="eventLocation"
                  name="eventLocation"
                  type="text"
                  placeholder="Enter event address"
                  value={formData.eventLocation}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="numberOfGuests">Number of Guests *</Label>
                <Input
                  id="numberOfGuests"
                  name="numberOfGuests"
                  type="number"
                  placeholder="50"
                  min="1"
                  value={formData.numberOfGuests}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Catering Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Catering Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="serviceType">Type of Service *</Label>
                <Select
                  value={formData.serviceType}
                  onValueChange={(value) => handleSelectChange("serviceType", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drop-off">Drop Off</SelectItem>
                    <SelectItem value="full-time">Full Time Catering</SelectItem>
                    <SelectItem value="bulk-food">Bulk Food Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="servingStyle">Serving Style *</Label>
                <Select
                  value={formData.servingStyle}
                  onValueChange={(value) => handleSelectChange("servingStyle", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select serving style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="packed">Packed</SelectItem>
                    <SelectItem value="buffet">Buffet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="menuPreference">Menu Preference *</Label>
              <Textarea
                id="menuPreference"
                name="menuPreference"
                placeholder="Describe your menu preferences (e.g., traditional Nigerian, continental mix, etc.)"
                rows={3}
                value={formData.menuPreference}
                onChange={handleChange}
                required
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="specificDishes">Specific Dishes Requested</Label>
              <Textarea
                id="specificDishes"
                name="specificDishes"
                placeholder="List any specific dishes you'd like included in the menu"
                rows={3}
                value={formData.specificDishes}
                onChange={handleChange}
                className="resize-none"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budgetRange">Budget Range (Estimated) *</Label>
                <Input
                  id="budgetRange"
                  name="budgetRange"
                  type="text"
                  placeholder="e.g., ₦100,000 - ₦200,000"
                  value={formData.budgetRange}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="referralSource">How did you hear about us? *</Label>
                <Select
                  value={formData.referralSource}
                  onValueChange={(value) => handleSelectChange("referralSource", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="others">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="specialNotes">
                Special Notes (Dietary Restrictions, Allergies, Additional Requests)
              </Label>
              <Textarea
                id="specialNotes"
                name="specialNotes"
                placeholder="Any dietary restrictions, allergies, or additional requests we should know about..."
                rows={4}
                value={formData.specialNotes}
                onChange={handleChange}
                className="resize-none"
              />
            </div>
          </div>

          {/* Note */}
          <div className="flex items-start gap-3 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <Clock className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Note:</span> We'll confirm availability and pricing within via your preferred method of contact.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

